import * as React from "react";
import { Dictionary } from "../../../../../../react-core/Core/Dictionary";
import { MoneyDisplayHelper } from "../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { Button } from "../../../../../../react-core/Html/Button";
import { Lightbox } from "../../../../../../react-core/Html/Components/Lightbox";
import { FormComponent } from "../../../../../../react-core/Html/FormComponent";
import { Money } from "../../../../../../Wrap/Core/DataTypes/Money";
import { Instrument } from "../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/Instrument";
import { IJsonDate } from "../../../../../../Wrap/System/IJsonDate";
import { ErrorMessagePopup } from "../../../../../Shared/Components/ErrorMessagePopup";
import { TableTempElement } from "../../../../../Shared/Components/TableTempElement";
import AdviserSiteTagManager from "../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { InvestmentsAction } from "../../PortfolioDashboard/Components/Investment/Action/InvestmentAction";
import { Contents } from "../../PortfolioDashboard/Components/Investment/Resource/Contents";
import { DepositProductType } from "../../../../../../Wrap/Constants/Enums/Domain/DepositProductType";
import { ExternalFundDocument } from "../Documents/Components/ExternalFundDocument";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";

interface IDepositDetailsPopupModalProps
{
    showBox: boolean;
    callBack: (val: boolean) => void;
    callerSource?: string;
    instrumentCode: string;
    productId: number;
    instrumentName: string;
    issuer: string;
    depositType: DepositProductType;
    term: number;
    grossRate: number;
    aerRate: number;
    minimumInvestment: number;
    maximumInvestment: number;
    maturityDate?: IJsonDate;
    holdingValue?: string;
}

interface IDepositDetailsPopupModalState
{
    isLoading: boolean;
    isShowErrorMessage: boolean;
}

export class DepositDetailsPopupModal
    extends FormComponent<IDepositDetailsPopupModalProps, IDepositDetailsPopupModalState>
{
    constructor(props: IDepositDetailsPopupModalProps)
    {
        super(props);

        this.state =
            {
                isLoading: true,
                isShowErrorMessage: false
            };
    }

    private renderLoading() {
        return <TableTempElement />;
    }

    public render(): JSX.Element {
        return (
            <Lightbox
                cssWrapper="is-default-lightbox is-wide"
                show={this.props.showBox}
                onClose={() => this.props.callBack(false)}
                title="Deposit information"
            >
                {this.renderModalBody()}
                {this.renderErrorMessage()}
            </Lightbox>
        );
    }

    private renderErrorMessage() {
        return (
            <ErrorMessagePopup
                showPopup={this.state.isShowErrorMessage}
                errorMessage={Contents.ErrorMessage_FileFailedToOpen}
                onClose={() =>
                {
                    this.setState({ isShowErrorMessage: false });
                }}
            />
        );
    }

    private renderModalBody() {
        return (
            <div>
                { this.renderDepositInfo() }
                { !this.isHoldingsPage() && this.renderInterestRateInformation() }
                { this.renderTermsAndConditions() }
                { this.renderAnnualEquivalentRateAndShariaInformation() }
            </div>
        );
    }

    private renderDepositInfo() {
        const {
            issuer,
            depositType,
            term,
            grossRate,
            aerRate,
            minimumInvestment,
            maximumInvestment} = this.props;

        return (
            <>
                <div className="panel">
                    <div className="row">
                        <div>
                            <div>
                                <span>Deposit taker: </span>
                                <span>{issuer}</span>
                            </div>
                            <div>
                                <span>Deposit type: </span>
                                <span>
                                {
                                    (() => {
                                        switch (depositType)
                                        {
                                            case DepositProductType.EasyAccess:
                                                return 'Easy Access';
                                            case DepositProductType.Notice:
                                                return 'Notice';
                                            case DepositProductType.Term:
                                                return 'Fixed Term';
                                        }
                                    })()
                                    }
                                </span>
                            </div>
                            {
                                (() => {
                                    switch (depositType)
                                    {
                                        case DepositProductType.EasyAccess:
                                            return null;
                                        case DepositProductType.Notice:
                                            return <div><span>Notice period: </span><span>{term} days</span></div>;
                                        case DepositProductType.Term:
                                            return <div><span>Term: </span><span>{term} months</span></div>;
                                    }
                                })()
                            }
                            <div>
                                <span>Gross interest rate: </span>
                                <span>
                                {
                                    new Intl.NumberFormat('en-GB', {
                                        style: 'percent',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    }).format(grossRate > 0 ? grossRate / 100 : 0)
                                }
                                </span>
                            </div>
                            <div>
                                <span>AER*: </span>
                                <span>
                                {
                                    new Intl.NumberFormat('en-GB', {
                                        style: 'percent',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    }).format(aerRate > 0 ? aerRate / 100 : 0)
                                }
                                </span>
                            </div>
                            <div>
                                <span>Minimum investment: </span>
                                <span>
                                    { MoneyDisplayHelper.FromatMoneyDisplay(
                                        minimumInvestment, CurrencyCode.GBP, true) }
                                </span>
                            </div>
                            <div>
                                <span>Maximum investment: </span>
                                <span>
                                    { MoneyDisplayHelper.FromatMoneyDisplay(
                                        maximumInvestment, CurrencyCode.GBP, true) }
                                </span>
                            </div>
                            {
                                this.isHoldingsPage() && this.renderMaturityDateAndHoldingValue()
                            }
                        </div>
                    </div>
                </div>
            </>
        );
    }

    private renderInterestRateInformation() {
        return (
            <>
                <div className="panel">
                    <div className="row">
                        <h3 className="mb-2">Information on cash account interest rates</h3>
                        <p>Please note that the cash deposits and rates offered are subject to change and the rates are not guaranteed until the funds reach the deposit taker. Cash deposits may be withdrawn at any time, if this happens and you have already placed an instruction to invest in a cash deposit with Bondsmith we will notify you and request alternative instructions. When selecting to invest in cash deposits please ensure you understand how they are protected. Further information on this can be found in the FSCS information sheet or on the FSCS website.</p>
                    </div>
                </div>
            </>
        );
    }

    private renderTermsAndConditions() {
        return (
            <>
                <div className="panel">
                    <div className="row" style={{flexDirection: "column"}}>
                        <h3 className="mb-2">Terms and conditions</h3>
                        <ExternalFundDocument
                            instrumentCodes={[this.props.instrumentCode]}
                            renderDownloadButtonSection={false}
                            data={null}
                            onChange={() => {}}
                            onRef={() => {}}/>
                    </div>
                </div>
            </>
        );
    }

    private renderAnnualEquivalentRateAndShariaInformation() {
        return (
            <>
                <div className="panel">
                    <div className="row">
                        <small>*AER stands for Annual Equivalent Rate. The AER illustrates what the interest rate would be if interest was paid and compounded once each year. It helps you compare interest rates across different products.  <strong>Note:</strong> As part of our range of deposit takers, we have Sharia products which offer an expected profit rate, rather than interest on their savings products, in order to comply with Sharia banking principles.</small>
                    </div>
                </div>
            </>
        );
    }

    private renderMaturityDateAndHoldingValue(): JSX.Element
    {
        return (
            <>
                {
                    this.props.depositType === DepositProductType.Term &&
                    <div>
                        <span>Maturity date: </span>
                        <span>
                            {
                                this.props.maturityDate
                                    ? JsonDateParser.getFormatDate(this.props.maturityDate, "dd/MM/yyyy")
                                    : ""
                            }
                        </span>
                    </div>
                }
                <div>
                    <span>Your deposit value: </span>
                    <span>
                        {
                            this.props.holdingValue
                        }
                    </span>
                </div>
            </>
        )
    }

    private isHoldingsPage(): boolean
    {
        return this.props.callerSource === "Holdings";
    }
}
